import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  VStack,
  Text,
  Button,
  Icon,
  HStack,
  Spinner,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { FiCheckCircle, FiHome, FiUser } from 'react-icons/fi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ProfileCard from '../shared/components/ProfileCard';

const CheckoutSuccess = () => {
  const [loading, setLoading] = useState(true);
  const [sessionData, setSessionData] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const planName = searchParams.get('plan');

    if (!sessionId) {
      setError('No session ID found');
      setLoading(false);
      return;
    }

    // Simulate session verification
    setTimeout(() => {
      setSessionData({
        sessionId,
        planName: planName || 'Pro',
        amount: planName === 'Enterprise' ? 99 : 29,
        currency: 'USD',
      });
      setLoading(false);
    }, 2000);
  }, [searchParams]);

  if (loading) {
    return (
      <Box bg="gray.50" minH="100vh" display="flex" alignItems="center" justifyContent="center">
        <VStack spacing={4}>
          <Spinner size="xl" color="orange.500" thickness="4px" />
          <Text color="gray.600">Verifying your payment...</Text>
        </VStack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box bg="gray.50" minH="100vh" display="flex" alignItems="center" justifyContent="center">
        <Container maxW="md">
          <ProfileCard>
            <VStack spacing={6} textAlign="center" py={8}>
              <Alert status="error">
                <AlertIcon />
                {error}
              </Alert>
              <Button onClick={() => navigate('/pricing')} colorScheme="orange">
                Back to Pricing
              </Button>
            </VStack>
          </ProfileCard>
        </Container>
      </Box>
    );
  }

  return (
    <Box bg="gray.50" minH="100vh" display="flex" alignItems="center" justifyContent="center">
      <Container maxW="md">
        <ProfileCard>
          <VStack spacing={8} textAlign="center" py={12}>
            {/* Success Icon */}
            <Box
              w={20}
              h={20}
              borderRadius="full"
              bg="green.50"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={FiCheckCircle} boxSize={10} color="green.500" />
            </Box>

            {/* Success Message */}
            <VStack spacing={4}>
              <Text fontSize="2xl" fontWeight="bold" color="gray.800">
                Payment Successful! 🎉
              </Text>
              <Text color="gray.600" lineHeight="1.6">
                Welcome to {sessionData?.planName} plan! Your subscription is now active and you have
                access to all premium features.
              </Text>
            </VStack>

            {/* Payment Details */}
            <Box
              w="100%"
              p={4}
              bg="gray.50"
              borderRadius="lg"
              border="1px solid"
              borderColor="gray.200"
            >
              <VStack spacing={2}>
                <HStack justify="space-between" w="100%">
                  <Text fontSize="sm" color="gray.600">Plan:</Text>
                  <Text fontSize="sm" fontWeight="medium">{sessionData?.planName}</Text>
                </HStack>
                <HStack justify="space-between" w="100%">
                  <Text fontSize="sm" color="gray.600">Amount:</Text>
                  <Text fontSize="sm" fontWeight="medium">
                    ${sessionData?.amount}/{sessionData?.planName === 'Enterprise' ? 'month' : 'month'}
                  </Text>
                </HStack>
                <HStack justify="space-between" w="100%">
                  <Text fontSize="sm" color="gray.600">Session ID:</Text>
                  <Text fontSize="xs" color="gray.500" fontFamily="mono">
                    {sessionData?.sessionId?.substring(0, 20)}...
                  </Text>
                </HStack>
              </VStack>
            </Box>

            {/* Action Buttons */}
            <VStack spacing={3} w="100%">
              <Button
                leftIcon={<Icon as={FiUser} />}
                colorScheme="orange"
                size="lg"
                w="100%"
                onClick={() => navigate('/me')}
              >
                Go to Profile
              </Button>
              <Button
                leftIcon={<Icon as={FiHome} />}
                variant="outline"
                size="lg"
                w="100%"
                onClick={() => navigate('/dashboard')}
              >
                Back to Dashboard
              </Button>
            </VStack>

            {/* Additional Info */}
            <Text fontSize="xs" color="gray.500" textAlign="center">
              You will receive a confirmation email shortly. If you have any questions,
              please contact our support team.
            </Text>
          </VStack>
        </ProfileCard>
      </Container>
    </Box>
  );
};

export default CheckoutSuccess;